<h2 mat-dialog-title>{{ 'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.BOOK_MEETING' | translate }}</h2>
<mat-dialog-content>
  <form [formGroup]="meetingScheduleForm">
    <glxy-form-field prefixIcon="search">
      <glxy-label>{{ 'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.CONTACT.PLACEHOLDER' | translate }}</glxy-label>
      <input
        type="text"
        matInput
        formControlName="userName"
        [matAutocomplete]="userNameAuto"
        placeholder="{{ 'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.CONTACT.PLACEHOLDER' | translate }}"
      />
      <mat-autocomplete #userNameAuto="matAutocomplete">
        @if (userList.length === 0 && !isContactLoading && getFormValue('userName') !== '') {
        <mat-option disabled="true">
          {{ 'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.CONTACT.EMPTY_STATE' | translate }}
        </mat-option>
        } @for (user of userList; track user) {
        <mat-option [value]="user.name" (click)="updateInvitee(user)" [disabled]="!user.email">
          {{ user.name }} @if (!user.email) {
          <div class="contact-missing-email">
            <mat-icon class="contact-missing-icon"> warning </mat-icon>
            {{ 'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.CONTACT.MISSING_EMAIL' | translate }}
          </div>
          } @if (user.email) {
          <span class="contact-email">{{ user.email }}</span>
          }
        </mat-option>
        } @if (isContactLoading) {
        <mat-option>
          <glxy-loading-spinner [inline]="true" />
        </mat-option>
        }
      </mat-autocomplete>
      <div class="guideline-add-contact-spacing"></div>
      <a (click)="createContact()">{{ 'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.ADD_CONTACT' | translate }}</a>
    </glxy-form-field>

    <glxy-form-field>
      <mat-chip-grid
        #additionalGuestEmailGrid
        [attr.aria-label]="'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.GUEST_EMAIL' | translate"
      >
        @for (emailID of inviteeEmails; track emailID) {
        <mat-chip-row [removable]="true" (removed)="removeInviteeEmail(emailID)">
          {{ emailID }}
          <mat-icon matChipRemove>cancel</mat-icon>
        </mat-chip-row>
        }

        <input
          placeholder="{{ 'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.GUEST_EMAIL.PLACEHOLDER' | translate }}"
          [matChipInputFor]="additionalGuestEmailGrid"
          [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
          [matChipInputAddOnBlur]="true"
          (matChipInputTokenEnd)="addInviteeEmail($event.value)"
          formControlName="additionalEmails"
        />
      </mat-chip-grid>
    </glxy-form-field>

    <glxy-form-field prefixIcon="search">
      <glxy-label>{{ 'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.EVENT_TYPE' | translate }}</glxy-label>
      <input
        type="text"
        matInput
        formControlName="eventType"
        [matAutocomplete]="eventTypeAuto"
        placeholder="{{ 'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.EVENT_TYPE.PLACEHOLDER' | translate }}"
      />
      <mat-autocomplete #eventTypeAuto="matAutocomplete" [displayWith]="displayMeetingType">
        <!-- Personal Events -->
        @if (filteredPersonalMeetingTypes$ | async; as personalMeetingTypes) { @if (personalMeetingTypes.length > 0) {
        <mat-optgroup
          class="event-type-optgroup"
          label="{{ 'MEETING_SCHEDULER.MEETING_LIST.PERSONAL_EVENTS' | translate }}"
        >
          @for (option of personalMeetingTypes; track option) {
          <mat-option [value]="option">
            {{ option.name }}
            <div class="badge-with-duration">{{ option.duration + 's' | duration }}</div>
          </mat-option>
          }
        </mat-optgroup>
        } }

        <!-- Team Events -->
        @if (filteredTeamMeetingTypes$ | async; as teamEventTypes ) { @if (teamEventTypes.length > 0) {
        <mat-optgroup
          class="event-type-optgroup"
          label="{{ 'MEETING_SCHEDULER.MEETING_LIST.TEAM_EVENTS' | translate }}"
        >
          @for (option of teamEventTypes; track option) {
          <mat-option [value]="option">
            {{ option.name }}
            <div class="badge-with-duration">{{ option.duration + 's' | duration }}</div>
          </mat-option>
          }
        </mat-optgroup>
        } }
      </mat-autocomplete>
    </glxy-form-field>

    <!-- Error message for event type with end date in the past -->
    @if (eventTypeEndDateInPast) {
    <glxy-error class="error-message">
      {{ 'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.EVENT_TYPE_END_DATE_PAST' | translate }} {{ eventTypeEndDate | date:
      'mediumDate' }}
    </glxy-error>
    } @if (eventType?.id && (eventType?.locationType === MeetingLocationType.IN_PERSON_USER_SITE ||
    eventType?.locationType === MeetingLocationType.IN_PERSON_CLIENT_SITE)) {
    <glxy-form-field>
      <glxy-label>{{ 'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.LOCATION' | translate }}</glxy-label>
      <input
        matInput
        formControlName="location"
        type="text"
        [value]="meetingScheduleForm.get('location')"
        [disabled]="eventType?.locationType === MeetingLocationType.IN_PERSON_USER_SITE"
        placeholder="{{ 'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.LOCATION.PLACEHOLDER' | translate }}"
      />
      @if (meetingScheduleForm.get('location')?.invalid && meetingScheduleForm.get('location')?.touched) {
      <glxy-error> {{ 'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.LOCATION.REQUIRED' | translate }} </glxy-error>
      } @if (locationGuideline) {
      <div class="guideline-add-contact-spacing">Guideline - {{locationGuideline}}</div>
      }
    </glxy-form-field>
    } @if (displayTeamMemberSelection && eventType?.meetingType !== TeamEventMeetingType.MULTI_HOST &&
    eventType?.meetingType !== TeamEventMeetingType.PRIORITY_ROUND_ROBIN) {
    <glxy-form-field>
      <glxy-label>{{ 'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.TEAM_MEMBER' | translate }}</glxy-label>
      <mat-select
        placeholder="{{ 'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.TEAM_MEMBER.PLACEHOLDER' | translate }}"
        formControlName="teamMemberID"
      >
        <mat-option [value]="'any'">
          <span class="any-team-member-label">
            {{ 'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.TEAM_MEMBER.ANY' | translate }}
          </span>
        </mat-option>
        @for (user of hostUsers; track user) {
        <mat-option [value]="user.userId"> {{ user.displayName }} </mat-option>
        }
      </mat-select>
    </glxy-form-field>
    } @if (displayTeamMemberSelection && eventType?.meetingType === TeamEventMeetingType.MULTI_HOST) {
    <glxy-form-field>
      <glxy-label>{{ 'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.HOST' | translate }}</glxy-label>
      <span> {{ hostUserDisplayNames() }}. </span>
    </glxy-form-field>
    } @if (displayTeamMemberSelection && eventType?.meetingType === TeamEventMeetingType.PRIORITY_ROUND_ROBIN &&
    hasMultipleHostsFlag) {
    <div class="priority-round-robin-navigation">
      <div
        class="priority-text"
        [innerHTML]="'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.PRIORITY_VIEWING_AVAILABILITY' | translate: { hostName: '<strong>' + currentHostDisplayName + '</strong>' }"
      ></div>
      <div class="navigation-buttons">
        <button
          mat-icon-button
          type="button"
          (click)="showPreviousHost()"
          [disabled]="!showPreviousButton"
          class="nav-button prev-button"
        >
          <mat-icon>chevron_left</mat-icon>
        </button>
        <button
          mat-icon-button
          type="button"
          (click)="showNextHost()"
          [disabled]="!showNextButton"
          class="nav-button next-button"
        >
          <mat-icon>chevron_right</mat-icon>
        </button>
      </div>
    </div>
    } @if (hostPreferences$ | async; as hostPreference) {
    <glxy-form-field>
      <glxy-timezone-selector
        [initialTimezone]="this.timezone"
        (timezoneChanged)="onTimezoneChanged($event)"
      ></glxy-timezone-selector>
    </glxy-form-field>
    }

    <div class="date-time-inputs">
      <div class="date-time-input-width">
        <meeting-scheduler-calendar-date-picker
          [config]="calendarConfig"
          [selectedDate]="selectedDate"
          [label]="'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.DATE_TIME'"
          [placeholder]="'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.DATE_TIME.DATE.PLACEHOLDER'"
          (dateChange)="onDateChange($event)"
          (availableTimeSlotsChange)="onAvailableTimeSlotsChange($event)"
        ></meeting-scheduler-calendar-date-picker>
      </div>
      <div class="date-time-input-width">
        <glxy-form-field>
          <mat-select
            placeholder="{{ 'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.DATE_TIME.TIME.PLACEHOLDER' | translate}}"
            formControlName="meetingTime"
          >
            @for (slot of availableTimeSlots; track slot) {
            <mat-option [value]="slot"> {{slot}} </mat-option>
            }
          </mat-select>
        </glxy-form-field>
      </div>
    </div>
  </form>
</mat-dialog-content>
<mat-dialog-actions>
  <button mat-stroked-button mat-dialog-close>{{ 'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.CLOSE' | translate }}</button>
  <button mat-flat-button color="primary" (click)="bookMeeting()" [disabled]="disableBookMeeting()">
    <glxy-button-loading-indicator [isLoading]="isBookingSubmitted">
      {{ 'MEETING_SCHEDULER.MEETING_ACTION.DIALOG.BOOK_MEETING' | translate }}
    </glxy-button-loading-indicator>
  </button>
</mat-dialog-actions>
